/* =============================================================================
 *  MailSyncHealth
 *
 *  Surfaces "a mailbox has stopped syncing" - a failure that used to be
 *  invisible (the refresh button said "up to date" while one mailbox had been
 *  dead for months). Three pieces share one data source (useMailSyncHealth):
 *
 *   - MailSyncHealthPill   DESKTOP. Renders nothing while everything is ok;
 *                          otherwise a compact topbar pill that opens an
 *                          anchored popover listing each unhealthy mailbox.
 *   - MailSyncHealthStrip  PHONE. Slim dismissible strip above the list. Only
 *                          for real trouble (warn / error): a parked, known
 *                          condition (a paused mailbox) is not worth a
 *                          permanent band on the tightest screen - it stays
 *                          reachable from the [...] sheet.
 *   - MailSyncHealthSheet  PHONE. The same details as a bottom sheet (opened by
 *                          the strip or by the more-actions item).
 *
 *  The popover and the sheet are one DOM: `.term-mail__row__menu` (the feed's
 *  own dropdown / bottom-sheet idiom), which TerminalEmailFeed.scss already
 *  turns into a fixed bottom sheet below 768px.
 * ============================================================================= */

import React, { useEffect, useRef, useState } from 'react';
import {
  MailboxHealth,
  MailboxStatus,
  MailSyncHealthData,
  attentionMailboxes,
  formatAgo,
  healthLevel,
  issueSignature,
  mailboxMeta,
  pillLabel,
  stripText,
} from '../utils/mailSyncModel';
import './MailSyncHealth.scss';

const STATUS_GLYPH: Record<MailboxStatus, string> = {
  ok: '+',
  degraded: '!',
  down: 'x',
  paused: '||',
};

const STATUS_FALLBACK_SUMMARY: Record<MailboxStatus, string> = {
  ok: 'Syncing normally.',
  degraded: 'Syncing, but with problems.',
  down: 'Not syncing.',
  paused: 'Paused - not being synced.',
};

// Dismissal of the phone strip survives the feed unmounting (in-memory), and a
// page reload / PWA cold start (localStorage, when available) for 24h. It is
// keyed by the SET of problems, so anything new brings the strip back, and it
// expires so a problem that is still there tomorrow is announced again.
const DISMISS_KEY = 'mail-sync-strip-dismissed';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

interface Dismissal { sig: string; at: number }

let memoryDismissal: Dismissal | null = null;

const isFresh = (d: Dismissal | null, now: number = Date.now()): d is Dismissal =>
  !!d && now - d.at < DISMISS_TTL_MS;

/** The current dismissal, or null (none, expired, or unreadable). */
function loadDismissal(): Dismissal | null {
  if (isFresh(memoryDismissal)) return memoryDismissal;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    const p = raw ? JSON.parse(raw) : null;
    if (p && typeof p.sig === 'string' && typeof p.at === 'number') {
      const d: Dismissal = { sig: p.sig, at: p.at };
      if (isFresh(d)) { memoryDismissal = d; return d; }
    }
  } catch { /* storage blocked / corrupt: the in-memory copy is all we have */ }
  return null;
}

function saveDismissal(sig: string): Dismissal {
  const d: Dismissal = { sig, at: Date.now() };
  memoryDismissal = d;
  try { window.localStorage.setItem(DISMISS_KEY, JSON.stringify(d)); } catch { /* per-viewer nicety only */ }
  return d;
}

/** Forget any dismissal (problem cleared; also used by tests). */
export function clearSyncStripDismissal(): void {
  memoryDismissal = null;
  try { window.localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
}

/* ── Shared details body ───────────────────────────────────────────────────── */

interface PanelProps {
  health: MailSyncHealthData;
  error: string | null;
  loading: boolean;
  onRefresh: () => void;
}

const SyncDetailsPanel: React.FC<PanelProps> = ({ health, error, loading, onRefresh }) => {
  const now = Date.now();
  const bad = attentionMailboxes(health);
  return (
    <div
      className="term-mail__row__menu term-mail__row__menu--sync term-mail__sync-details"
      role="dialog"
      aria-label="Mail sync status"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="term-mail__row__menu-group">mail sync</div>
      {bad.length === 0 ? (
        <div className="term-mail__row__menu-note">
          all {health.mailboxes.length} mailboxes healthy
        </div>
      ) : (
        bad.map((m: MailboxHealth) => (
          <div
            key={m.mailbox}
            className={`term-mail__sync-item term-mail__sync-item--${m.status}`}
            title={m.issues.map((i) => i.message).join('\n') || undefined}
          >
            <span className="term-mail__sync-item__glyph" aria-hidden>{STATUS_GLYPH[m.status]}</span>
            <div className="term-mail__sync-item__body">
              <div className="term-mail__sync-item__mailbox">{m.mailbox}</div>
              <div className="term-mail__sync-item__summary">{m.summary || STATUS_FALLBACK_SUMMARY[m.status]}</div>
              <div className="term-mail__sync-item__meta">{mailboxMeta(m, now)}</div>
            </div>
          </div>
        ))
      )}
      <div className="term-mail__row__menu-divider" />
      <div className="term-mail__sync-details__foot">
        checked {formatAgo(health.generatedAt, now)}
        {error && (
          <span className="term-mail__sync-details__err" title={error}> {'·'} couldn't refresh</span>
        )}
      </div>
      <button
        type="button"
        className="term-mail__row__menu-item"
        disabled={loading}
        onClick={onRefresh}
      >
        {loading ? 'Checking...' : 'Recheck now'}
      </button>
    </div>
  );
};

/* ── Desktop: topbar pill + anchored popover ───────────────────────────────── */

interface PillProps {
  health: MailSyncHealthData | null;
  error: string | null;
  loading: boolean;
  onRefresh: () => void;
  /** The feed's bracket helper: "[x]" under terminal, bare "x" under graphite. */
  br: (s: string) => string;
}

export const MailSyncHealthPill: React.FC<PillProps> = ({ health, error, loading, onRefresh, br }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const level = healthLevel(health);
  const visible = !!health && level !== 'ok';

  // Fold the popover away when the problem clears underneath it.
  useEffect(() => { if (!visible) setOpen(false); }, [visible]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && e.target instanceof Node && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!visible || !health) return null;
  return (
    <span className="term-mail__row__menu-wrap term-mail__syncpill-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`term-mail__action term-mail__syncpill term-mail__syncpill--${level}`}
        onClick={() => setOpen((v) => !v)}
        title={stripText(health).replace(' - tap for details', '')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {br(pillLabel(level))}
      </button>
      {open && <SyncDetailsPanel health={health} error={error} loading={loading} onRefresh={onRefresh} />}
    </span>
  );
};

/* ── Phone: strip ──────────────────────────────────────────────────────────── */

interface StripProps {
  health: MailSyncHealthData | null;
  onOpen: () => void;
}

export const MailSyncHealthStrip: React.FC<StripProps> = ({ health, onOpen }) => {
  const level = healthLevel(health);
  const sig = issueSignature(health);
  const [dismissal, setDismissal] = useState<Dismissal | null>(loadDismissal);

  // Healthy again: forget the dismissal so the same problem, if it returns,
  // is announced instead of staying silenced.
  useEffect(() => {
    if (health && level === 'ok') {
      clearSyncStripDismissal();
      setDismissal(null);
    }
  }, [health, level]);

  // 'info' (only paused mailboxes, everything else fine) is a parked condition
  // the owner already knows about: no band on a phone for it - the [...] sheet
  // item and the details sheet still show it. Dismissal only counts while fresh
  // (a tab left open for days re-announces a problem that is still there).
  if (!health || level === 'ok' || level === 'info') return null;
  if (dismissal && dismissal.sig === sig && isFresh(dismissal)) return null;
  return (
    <div className={`term-mail__health-strip term-mail__health-strip--${level}`} role="status">
      <button type="button" className="term-mail__health-strip__main" onClick={onOpen}>
        <span className="term-mail__health-strip__glyph" aria-hidden>
          {level === 'error' ? 'x' : '!'}
        </span>
        <span className="term-mail__health-strip__text">{stripText(health)}</span>
      </button>
      <button
        type="button"
        className="term-mail__health-strip__close"
        aria-label="Dismiss sync notice"
        onClick={() => setDismissal(saveDismissal(sig))}
      >
        x
      </button>
    </div>
  );
};

/* ── Phone: bottom sheet ───────────────────────────────────────────────────── */

interface SheetProps {
  health: MailSyncHealthData | null;
  error: string | null;
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const MailSyncHealthSheet: React.FC<SheetProps> = ({ health, error, loading, open, onClose, onRefresh }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !health) return null;
  return (
    <>
      {/* Scrim swallows the dismissing tap so it cannot fall through and open
          the row underneath (same reason as the row / top menus). */}
      <div className="term-mail__row__menu-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <SyncDetailsPanel health={health} error={error} loading={loading} onRefresh={onRefresh} />
    </>
  );
};
